import React, { useState, useEffect } from 'react';
import { Menu, Phone, Star, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from '@/components/ui/sheet';

const LOGO_URL = "/logo.png";

// External rating profile URLs - replace with the home's exact listing URLs whenever available
const CQC_PROFILE_URL = "https://www.cqc.org.uk/location/1-12436440703";
const CAREHOME_PROFILE_URL = "https://www.carehome.co.uk/carehome.cfm/searchazref/20003515ANCA";

const navLinks = [
  { href: '#about', label: 'About' },
  { href: '#services', label: 'Care Services' },
  { href: '#facilities', label: 'Facilities' },
  { href: '#cqc', label: 'CQC Rating' },
  { href: '#reviews', label: 'Reviews' },
  { href: '#careers', label: 'Careers' },
  { href: '#contact', label: 'Contact' },
];

const Navigation = ({ activeSection }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsOpen(false);
  };

  return (
    <header
      data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-md'
          : 'bg-transparent'
      }`}
    >
      {/* Nav bar */}
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between gap-4 py-3">
          {/* Logo and Name + trust chips */}
          <div className="flex items-center gap-4 shrink-0 min-w-0">
            <a
              href="#hero"
              onClick={(e) => {
                e.preventDefault();
                scrollToSection('#hero');
              }}
              className="flex items-center gap-3 shrink-0"
              data-testid="logo-link"
            >
              <img
                src={LOGO_URL}
                alt="Abney Low Nursing Home"
                className="h-12 sm:h-14 w-auto"
              />
              <div className={`leading-tight ${isScrolled ? 'text-[#1e3a5f]' : 'text-white'}`}>
                <div className="text-base sm:text-lg font-bold">
                  Abney Low
                </div>
                <div className="text-[11px] sm:text-xs font-medium opacity-90 tracking-wide uppercase">
                  Nursing Home
                </div>
              </div>
            </a>

            {/* Trust chips: CQC + carehome.co.uk — sit beside the brand, blend with header */}
            <div className="hidden md:flex items-center gap-2.5 pl-4 border-l border-white/15">
              <a
                href={CQC_PROFILE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-xl transition-colors border ${
                  isScrolled
                    ? 'bg-emerald-50 border-emerald-200 hover:bg-emerald-100'
                    : 'bg-white/10 border-white/25 hover:bg-white/20 backdrop-blur-sm'
                }`}
                data-testid="cqc-badge-link"
                aria-label="View our CQC rating"
              >
                <ShieldCheck className={`w-5 h-5 shrink-0 ${isScrolled ? 'text-emerald-600' : 'text-emerald-300'}`} />
                <div className="leading-tight text-left">
                  <div className={`text-[9px] font-semibold uppercase tracking-wider ${
                    isScrolled ? 'text-emerald-700/80' : 'text-white/70'
                  }`}>
                    CQC Rating
                  </div>
                  <div className={`text-sm font-bold ${isScrolled ? 'text-emerald-800' : 'text-white'}`}>
                    Good
                  </div>
                </div>
              </a>

              <a
                href={CAREHOME_PROFILE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-xl transition-colors border ${
                  isScrolled
                    ? 'bg-[#c9a227]/10 border-[#c9a227]/30 hover:bg-[#c9a227]/15'
                    : 'bg-white/10 border-white/25 hover:bg-white/20 backdrop-blur-sm'
                }`}
                data-testid="carehome-badge-link"
                aria-label="View our reviews on carehome.co.uk"
              >
                <Star className="w-5 h-5 text-[#c9a227] fill-[#c9a227] shrink-0" />
                <div className="leading-tight text-left">
                  <div className={`text-[9px] font-semibold uppercase tracking-wider ${
                    isScrolled ? 'text-[#8a6f1a]/80' : 'text-white/70'
                  }`}>
                    carehome.co.uk
                  </div>
                  <div className={`text-sm font-bold ${isScrolled ? 'text-[#8a6f1a]' : 'text-white'}`}>
                    5.0 / 5
                  </div>
                </div>
              </a>
            </div>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-5 xl:gap-7">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection(link.href);
                }}
                className={`text-sm font-medium transition-colors duration-300 whitespace-nowrap ${
                  isScrolled
                    ? 'text-[#1e3a5f] hover:text-[#c9a227]'
                    : 'text-white hover:text-[#c9a227]'
                } ${activeSection === link.href.slice(1) ? 'text-[#c9a227]' : ''}`}
                data-testid={`nav-link-${link.label.toLowerCase().replace(' ', '-')}`}
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center gap-4 shrink-0">
            <a
              href="tel:01516324504"
              className={`hidden xl:flex items-center gap-2 text-sm font-medium whitespace-nowrap ${
                isScrolled ? 'text-[#1e3a5f]' : 'text-white'
              }`}
              data-testid="phone-link"
            >
              <Phone className="w-4 h-4" />
              0151 632 4504
            </a>
            <Button
              onClick={() => scrollToSection('#contact')}
              className="bg-[#c9a227] text-[#1e3a5f] hover:bg-[#d4b03a] rounded-full px-5 py-2 text-sm font-semibold whitespace-nowrap"
              data-testid="book-visit-nav-btn"
            >
              Book a Visit
            </Button>
          </div>

          {/* Mobile Menu */}
          <div className="lg:hidden">
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className={isScrolled ? 'text-[#1e3a5f]' : 'text-white'}
                  data-testid="mobile-menu-btn"
                >
                  <Menu className="w-6 h-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-full sm:w-80 bg-white">
                <div className="flex flex-col h-full">
                  <div className="flex items-center gap-3 py-4 border-b">
                    <img
                      src={LOGO_URL}
                      alt="Abney Low Nursing Home"
                      className="h-12 w-auto"
                    />
                    <div className="text-[#1e3a5f] leading-tight">
                      <div className="text-base font-bold">
                        Abney Low
                      </div>
                      <div className="text-xs uppercase tracking-wide">Nursing Home</div>
                    </div>
                  </div>

                  {/* Trust chips in mobile drawer */}
                  <div className="flex items-center gap-2 py-3 border-b">
                    <a
                      href={CQC_PROFILE_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-3 py-2 rounded-xl bg-emerald-50 border border-emerald-200"
                      data-testid="mobile-cqc-badge-link"
                    >
                      <ShieldCheck className="w-5 h-5 text-emerald-600 shrink-0" />
                      <div className="leading-tight text-left">
                        <div className="text-[9px] font-semibold uppercase tracking-wider text-emerald-700/80">CQC Rating</div>
                        <div className="text-sm font-bold text-emerald-800">Good</div>
                      </div>
                    </a>
                    <a
                      href={CAREHOME_PROFILE_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-3 py-2 rounded-xl bg-[#c9a227]/10 border border-[#c9a227]/30"
                      data-testid="mobile-carehome-badge-link"
                    >
                      <Star className="w-5 h-5 text-[#c9a227] fill-[#c9a227] shrink-0" />
                      <div className="leading-tight text-left">
                        <div className="text-[9px] font-semibold uppercase tracking-wider text-[#8a6f1a]/80">carehome.co.uk</div>
                        <div className="text-sm font-bold text-[#8a6f1a]">5.0 / 5</div>
                      </div>
                    </a>
                  </div>
                  <nav className="flex flex-col py-6">
                    {navLinks.map((link) => (
                      <a
                        key={link.href}
                        href={link.href}
                        onClick={(e) => {
                          e.preventDefault();
                          scrollToSection(link.href);
                        }}
                        className="py-4 px-2 text-lg font-medium text-[#1e3a5f] hover:text-[#c9a227] hover:bg-slate-50 transition-colors rounded-lg"
                        data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(' ', '-')}`}
                      >
                        {link.label}
                      </a>
                    ))}
                  </nav>
                  <div className="mt-auto pb-8 space-y-4">
                    <a
                      href="tel:01516324504"
                      className="flex items-center justify-center gap-2 py-3 text-[#1e3a5f] font-medium"
                    >
                      <Phone className="w-5 h-5" />
                      0151 632 4504
                    </a>
                    <Button
                      onClick={() => scrollToSection('#contact')}
                      className="w-full bg-[#c9a227] text-[#1e3a5f] hover:bg-[#d4b03a] rounded-full py-3 font-medium"
                      data-testid="mobile-book-visit-btn"
                    >
                      Book a Visit
                    </Button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </nav>
    </header>
  );
};

export default Navigation;
